import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Spinner } from "@/shared/components/ui/spinner";
import type { DailyRentalData } from "../../types/dashboard.types";

const BAR_COLOR = "#3b82f6";
const LINE_COLOR = "#a78bfa";
const TICK = "#64748b";
const GRID = "rgba(148,163,184,0.08)";

function brl(v: number) {
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function brlShort(v: number) {
  if (v >= 1000) return `R$${(v / 1000).toFixed(1)}k`;
  return `R$${v.toFixed(0)}`;
}

// ─── Tooltip escuro ───────────────────────────────────────────────────────────

interface TooltipRow {
  label: string;
  revenue: number;
  ticketMedio: number;
  count: number;
}

interface DarkTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: TooltipRow }>;
}

const DarkTooltip = ({ active, payload }: DarkTooltipProps) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  if (!d.count) return null;

  return (
    <div
      style={{
        backgroundColor: "#0f172a",
        border: "1px solid #1e293b",
        borderRadius: "10px",
        padding: "10px 14px",
        fontSize: 12,
        lineHeight: 1.8,
        boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
        minWidth: 168,
      }}
    >
      <p
        style={{
          color: "#94a3b8",
          fontWeight: 500,
          marginBottom: 4,
          fontSize: 11,
        }}
      >
        {d.label}
      </p>
      <p style={{ color: "#93c5fd" }}>
        <span
          style={{ color: "#64748b", minWidth: 84, display: "inline-block" }}
        >
          Receita
        </span>
        {brl(d.revenue)}
      </p>
      <p style={{ color: "#c4b5fd" }}>
        <span
          style={{ color: "#64748b", minWidth: 84, display: "inline-block" }}
        >
          Ticket médio
        </span>
        {brl(d.ticketMedio)}
      </p>
      <p style={{ color: "#94a3b8" }}>
        <span
          style={{ color: "#64748b", minWidth: 84, display: "inline-block" }}
        >
          Locações
        </span>
        {d.count}
      </p>
    </div>
  );
};

// ─── Dot customizado — só renderiza onde há dados ────────────────────────────

interface DotProps {
  cx?: number;
  cy?: number;
  payload?: DailyRentalData;
}

const TicketDot = ({ cx, cy, payload }: DotProps) => {
  if (!payload?.count || cx === undefined || cy === undefined) return <g />;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill={LINE_COLOR}
      stroke="#0f172a"
      strokeWidth={1.5}
    />
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────

interface RevenueTicketChartProps {
  data: DailyRentalData[];
  loading?: boolean;
}

export const RevenueTicketChart = ({
  data,
  loading,
}: RevenueTicketChartProps) => {
  const hasData = data.some((d) => d.revenue > 0);
  const activeDays = data.filter((d) => d.count > 0).length;
  const showLine = activeDays > 1;

  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">
            Receita e ticket médio
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Barras&nbsp;=&nbsp;receita&nbsp;&nbsp;·&nbsp;&nbsp;Linha&nbsp;=&nbsp;ticket
            médio
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex h-52 items-center justify-center">
          <Spinner className="size-6" />
        </div>
      ) : !hasData ? (
        <div className="flex h-52 items-center justify-center text-sm text-muted-foreground">
          Sem dados financeiros na semana atual
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart
            data={data}
            margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
            barCategoryGap="40%"
          >
            <CartesianGrid stroke={GRID} vertical={false} />

            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: TICK }}
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              padding={{ left: 16, right: 16 }}
            />

            <YAxis
              yAxisId="left"
              tick={{ fontSize: 10, fill: TICK }}
              axisLine={false}
              tickLine={false}
              tickCount={4}
              tickFormatter={brlShort}
              width={48}
            />

            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 10, fill: TICK }}
              axisLine={false}
              tickLine={false}
              tickCount={4}
              tickFormatter={brlShort}
              width={48}
            />

            <Tooltip
              content={<DarkTooltip />}
              cursor={{ fill: "rgba(148,163,184,0.06)", radius: 4 }}
            />

            <Legend
              iconSize={8}
              wrapperStyle={{ fontSize: 11, color: TICK, paddingTop: 12 }}
              formatter={(v) => (v === "revenue" ? "Receita" : "Ticket médio")}
            />

            <Bar
              yAxisId="left"
              dataKey="revenue"
              fill={BAR_COLOR}
              radius={[4, 4, 0, 0]}
              maxBarSize={52}
              opacity={0.85}
            />

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="ticketMedio"
              stroke={showLine ? LINE_COLOR : "transparent"}
              strokeWidth={showLine ? 2 : 0}
              dot={<TicketDot />}
              activeDot={
                showLine ? { r: 5, strokeWidth: 0, fill: LINE_COLOR } : false
              }
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
